import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Sliders, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const LIMIT_FIELDS = [
  ["ai_messages_monthly", "AI messages / mo"],
  ["storage_bytes", "Storage (bytes)"],
  ["generated_docs_monthly", "Docs / mo"],
  ["contract_generations_monthly", "Contracts / mo"],
  ["report_exports_monthly", "Report exports / mo"],
  ["team_members", "Team seats"],
  ["businesses", "Businesses"],
];

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#121214] px-5 py-4" data-testid={`subanalytics-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-xs text-[#71717A]">{label}</div>
      <div className={`text-2xl font-heading mt-1 ${accent ? "text-[#D4AF37]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function PlanEditor({ plan, allModules, allFeatures, featureCatalog, moduleLabels, onSave }) {
  const [modules, setModules] = useState(new Set(plan.modules || []));
  const [features, setFeatures] = useState(new Set(plan.features || []));
  const [limits, setLimits] = useState({ ...plan.limits });
  const [saving, setSaving] = useState(false);

  const toggle = (set, setter, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(plan.id, {
        modules: [...modules], features: [...features],
        limits: Object.fromEntries(Object.entries(limits).map(([k, v]) =>
          [k, v === "" || v === null ? null : Number(v)])),
      });
      toast.success(`${plan.name} plan updated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={`plan-editor-${plan.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-white font-medium">{plan.name}</div>
          <div className="text-xs text-[#71717A]">${plan.price}/mo</div>
        </div>
        <Button onClick={save} disabled={saving} data-testid={`plan-save-${plan.id}`}
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-8 text-xs">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save</>}
        </Button>
      </div>

      <div className="text-[11px] uppercase tracking-wide text-[#71717A] mb-2">Modules</div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {allModules.map((m) => (
          <button key={m} onClick={() => toggle(modules, setModules, m)} data-testid={`plan-${plan.id}-module-${m}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${modules.has(m) ? "fv-gold-gradient text-[#0A0A0B] font-semibold border-transparent" : "border-white/10 text-[#A1A1AA]"}`}>
            {moduleLabels[m] || m}
          </button>
        ))}
      </div>

      <div className="text-[11px] uppercase tracking-wide text-[#71717A] mb-2">Features</div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {allFeatures.map((f) => (
          <button key={f} onClick={() => toggle(features, setFeatures, f)} data-testid={`plan-${plan.id}-feature-${f}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${features.has(f) ? "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40" : "border-white/10 text-[#A1A1AA]"}`}>
            {featureCatalog[f] || f}
          </button>
        ))}
      </div>

      <div className="text-[11px] uppercase tracking-wide text-[#71717A] mb-2">Limits (blank = unlimited)</div>
      <div className="grid grid-cols-2 gap-2">
        {LIMIT_FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="text-[10px] text-[#71717A]">{label}</label>
            <Input value={limits[key] ?? ""} onChange={(e) => setLimits({ ...limits, [key]: e.target.value })}
              placeholder="∞" data-testid={`plan-${plan.id}-limit-${key}`}
              className="bg-[#0d0d0f] border-white/10 text-white h-8 text-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPlans() {
  const [cfg, setCfg] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);

  const loadCfg = useCallback(() => api.get("/admin/plan-config").then((r) => { setCfg(r.data); setSettings(r.data.settings); }).catch(() => {}), []);
  const loadAnalytics = useCallback(() => api.get("/admin/subscription/analytics").then((r) => setAnalytics(r.data)).catch(() => {}), []);
  useEffect(() => { loadCfg(); loadAnalytics(); }, [loadCfg, loadAnalytics]);

  const savePlan = async (planId, override) => {
    await api.put("/admin/plan-config", { plan_id: planId, ...override });
    loadCfg();
  };
  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await api.put("/admin/plan-settings", patch); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadCfg(); }
  };

  if (!cfg || !analytics) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;

  const plans = ["starter", "growth", "enterprise"].map((p) => cfg.plans[p]);

  return (
    <div className="space-y-8" data-testid="admin-plans">
      {/* Analytics */}
      <div>
        <h3 className="text-white font-medium mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Subscription analytics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Real MRR (live Stripe)" value={`$${(analytics.real?.mrr ?? 0).toLocaleString()}`} accent />
          <Stat label="Real ARR (live Stripe)" value={`$${(analytics.real?.arr ?? 0).toLocaleString()}`} accent />
          <Stat label="New MRR today" value={`$${(analytics.real?.new_mrr?.today ?? 0).toLocaleString()}`} accent />
          <Stat label="New MRR this week" value={`$${(analytics.real?.new_mrr?.week ?? 0).toLocaleString()}`} />
          <Stat label="New MRR this month" value={`$${(analytics.real?.new_mrr?.month ?? 0).toLocaleString()}`} />
          <Stat label="Verified paid subs" value={analytics.verified_paid_subscribers ?? 0} accent />
          <Stat label="Total accounts" value={analytics.total_accounts ?? analytics.total_subscribers} />
          <Stat label="New paid today" value={analytics.real?.new_paid?.today ?? 0} accent />
          <Stat label="New paid this week" value={analytics.real?.new_paid?.week ?? 0} />
          <Stat label="New paid this month" value={analytics.real?.new_paid?.month ?? 0} />
          <Stat label="Seed/demo records" value={analytics.seed_demo_records ?? 0} />
          <Stat label="Real annual subs" value={analytics.real?.annual ?? 0} />
          <Stat label="Real monthly subs" value={analytics.real?.monthly ?? 0} />
          <Stat label="Trial conversion" value={`${analytics.trial_conversion_rate}%`} />
          <Stat label="Churn rate" value={`${analytics.churn_rate}%`} />
          <Stat label="Recovery rate" value={`${analytics.recovery_rate}%`} />
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-3">
          <div className="rounded-xl border border-white/8 bg-[#121214] p-4">
            <div className="text-xs text-[#71717A] mb-2">MRR by plan</div>
            {analytics.by_plan.map((p) => (
              <div key={p.plan} className="flex justify-between text-sm py-1">
                <span className="text-[#D4D4D8] capitalize">{p.plan}</span>
                <span className="text-white">{p.count} · <span className="text-[#D4AF37]">${p.mrr.toLocaleString()}</span></span>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-white/8 bg-[#121214] p-4" data-testid="upgrade-funnel">
            <div className="text-xs text-[#71717A] mb-2">Upgrade funnel</div>
            {[["Impressions", analytics.funnel?.impressions], ["Clicks", analytics.funnel?.clicks],
              ["Conversions", analytics.funnel?.conversions], ["Annual conversions", analytics.funnel?.annual_conversions]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm py-1"><span className="text-[#D4D4D8]">{l}</span><span className="text-white">{v ?? 0}</span></div>
            ))}
          </div>
          <div className="rounded-xl border border-white/8 bg-[#121214] p-4">
            <div className="text-xs text-[#71717A] mb-2">Top cancellation reasons</div>
            {analytics.cancellation_reasons.length ? analytics.cancellation_reasons.map((r) => (
              <div key={r.reason} className="flex justify-between text-sm py-1">
                <span className="text-[#D4D4D8] capitalize">{r.reason.replace(/_/g, " ")}</span>
                <span className="text-white">{r.count}</span>
              </div>
            )) : <p className="text-sm text-[#52525B]">No cancellations recorded.</p>}
          </div>
        </div>
      </div>

      {/* Trial / grace settings */}
      {settings && (
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="plan-settings">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Sliders className="w-4 h-4 text-[#D4AF37]" /> Trial & lifecycle settings</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="flex items-center justify-between md:col-span-2">
              <span className="text-sm text-[#D4D4D8]">Free trials enabled</span>
              <Switch checked={settings.trial_enabled} onCheckedChange={(v) => saveSettings({ trial_enabled: v })} data-testid="setting-trial-enabled" />
            </div>
            <div className="flex items-center justify-between md:col-span-2">
              <span className="text-sm text-[#D4D4D8]">Trial requires card</span>
              <Switch checked={settings.trial_requires_card} onCheckedChange={(v) => saveSettings({ trial_requires_card: v })} data-testid="setting-trial-card" />
            </div>
            {[["trial_days", "Trial days"], ["grace_days", "Grace days"], ["retention_days", "Retention days"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs text-[#71717A]">{l}</label>
                <Input type="number" defaultValue={settings[k]} data-testid={`setting-${k}`}
                  onBlur={(e) => saveSettings({ [k]: Number(e.target.value) })}
                  className="bg-[#0d0d0f] border-white/10 text-white h-9 mt-1" />
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#D4D4D8]">Downgrade at period end</span>
              <Switch checked={settings.downgrade_at_period_end} onCheckedChange={(v) => saveSettings({ downgrade_at_period_end: v })} data-testid="setting-downgrade-eop" />
            </div>
          </div>
        </div>
      )}

      {/* Plan configuration */}
      <div>
        <h3 className="text-white font-medium mb-3">Plan configuration</h3>
        <div className="grid lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <PlanEditor key={p.id} plan={p} allModules={cfg.all_modules} allFeatures={cfg.all_features}
              featureCatalog={cfg.feature_catalog} moduleLabels={cfg.module_labels} onSave={savePlan} />
          ))}
        </div>
      </div>
    </div>
  );
}
